import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  Check,
  ClipboardCheck,
  FileUp,
  Globe2,
  Layers3,
  Play,
  ShieldCheck,
  Sparkles,
  Trophy,
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import DemoLink from '@/components/DemoLink';
import SEOHead from '@/components/SEOHead';
import SportSelectorWidget from '@/components/SportSelectorWidget';
import ProductShotFigure from '@/components/marketing/ProductShotFigure';
import { Button } from '@/components/ui/button';

const matchCentreFeatures = [
  'Live and final scores',
  'Standings, schedules, and form',
  'Team and player pages',
  'Sport-specific box scores',
  'Playoff brackets',
  'Match previews and recaps',
  'Season archives',
  'Sponsor placements',
];

const operatingSteps = [
  'Import clubs, teams, and players.',
  'Generate a balanced schedule or import your fixtures.',
  'Give each role, including clubs, the access it needs.',
  'Score and publish matches from one workspace.',
  'Let standings, statistics, and stories update automatically.',
];

const Index = () => {
  const location = useLocation();

  // Navbar links to in-page sections (Match Centre, Competition Operations)
  // use a "/#section-id" hash. React Router's Link intercepts the click and
  // pushes history state instead of letting the browser do its native
  // same-document anchor scroll, so without this the URL hash changes but
  // the page never scrolls, on this page or when arriving from another one.
  useEffect(() => {
    if (!location.hash) return;
    const target = document.querySelector(location.hash);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash]);

  return (
  <div className="min-h-screen flex flex-col bg-background">
    <SEOHead
      title="StatHarbour | Competition Management and Live League Websites"
      description="Run schedules, scores, standings, player statistics, playoffs, and sponsor reporting from one platform, with an official branded match centre for fans."
      keywords={[
        'competition management platform',
        'semi-professional league software',
        'official match centre',
        'live league website',
        'sports statistics platform',
        'sponsor reporting',
      ]}
      canonicalUrl="https://statharbour.com"
      ogType="website"
    />
    <Navbar />

    <main>
      <section className="relative overflow-hidden border-b bg-gradient-to-br from-primary/10 via-background to-background py-20 lg:py-28">
        <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_75%_25%,hsl(var(--primary)/0.25),transparent_34%)]" />
        <div className="container relative mx-auto px-4">
          <div className="mx-auto max-w-5xl text-center">
            <p className="mb-5 text-sm font-semibold uppercase tracking-[0.16em] text-primary">
              Built for semi-professional and ambitious regional competitions
            </p>
            <h1 className="text-4xl font-bold leading-[1.05] tracking-tight md:text-6xl lg:text-7xl">
              Run the competition. Own the match day.
            </h1>
            <p className="mx-auto mt-7 max-w-3xl text-lg leading-8 text-muted-foreground md:text-xl">
              StatHarbour brings schedules, live scores, standings, player statistics,
              playoffs, and sponsor inventory into one official match centre. Competition
              staff run the season from one workspace. Fans follow every game on a fast,
              branded public site.
            </p>
            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
              <DemoLink>
                <Button size="lg" className="w-full gap-2 sm:w-auto">
                  <Play className="h-4 w-4" /> Explore a live competition
                </Button>
              </DemoLink>
              <Button asChild size="lg" variant="outline" className="w-full gap-2 sm:w-auto">
                <Link to="/signup">
                  Start free <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Guided migration review</span>
              <span className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Branded hosted match centre</span>
              <span className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> No fan login required</span>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b bg-card py-5" aria-label="Supported sports">
        <div className="container mx-auto flex flex-wrap items-center justify-center gap-x-7 gap-y-2 px-4 text-sm font-semibold text-muted-foreground">
          <span>Basketball</span><span aria-hidden="true">•</span>
          <span>Baseball</span><span aria-hidden="true">•</span>
          <span>Softball</span><span aria-hidden="true">•</span>
          <span>Flag football</span><span aria-hidden="true">•</span>
          <span>Soccer</span>
        </div>
      </section>

      <section id="product" className="scroll-mt-24 py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
              Your competition is serious. Its digital presence should be too.
            </h2>
            <p className="mt-5 text-lg leading-8 text-muted-foreground">
              A spreadsheet can hold fixtures. It cannot turn a box score into a story,
              keep every public surface in sync, or show a sponsor what their placement delivered.
            </p>
          </div>
          <div className="mx-auto mt-12 grid max-w-6xl gap-6 md:grid-cols-3">
            {[
              {
                icon: ClipboardCheck,
                title: 'One result, updated everywhere',
                body: 'Enter a score once. Standings, statistics, brackets, and the public match centre update together.',
              },
              {
                icon: ShieldCheck,
                title: 'Controlled access for every role',
                body: 'Give clubs, scorekeepers, and statisticians exactly what they need, with a change history the competition office can check.',
              },
              {
                icon: BarChart3,
                title: 'A public product sponsors can buy into',
                body: 'Place partners across the match centre and report views and clicks by placement and date.',
              },
            ].map(({ icon: Icon, title, body }) => (
              <article key={title} className="rounded-2xl border bg-card p-7 shadow-sm">
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-semibold">{title}</h3>
                <p className="mt-3 leading-7 text-muted-foreground">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="match-centre" className="scroll-mt-24 border-y bg-slate-950 py-20 text-white">
        <div className="container mx-auto grid items-center gap-12 px-4 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-400">
              The official home of your competition
            </p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight md:text-5xl">
              Every fixture, result, and story in one place
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
              Publish scores, standings, schedules, playoff brackets, team pages,
              player leaders, box scores, and match reports on a public site carrying
              your competition&apos;s colours and identity. It works on match day and on mobile,
              without asking fans to create an account.
            </p>
            <div className="mt-8">
              <DemoLink>
                <Button size="lg" variant="secondary" className="gap-2">
                  See the live match centre <ArrowRight className="h-4 w-4" />
                </Button>
              </DemoLink>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {matchCentreFeatures.map((feature) => (
              <div key={feature} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-4">
                <Check className="h-4 w-4 shrink-0 text-emerald-400" />
                <span className="text-sm font-medium text-slate-100">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="operations" className="scroll-mt-24 py-20">
        <div className="container mx-auto grid gap-12 px-4 lg:grid-cols-2">
          <div>
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Layers3 className="h-6 w-6" />
            </div>
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Run the season from one source of truth</h2>
            <p className="mt-5 text-lg leading-8 text-muted-foreground">
              Build the competition, publish the schedule, score matches, and keep
              the public experience in sync. StatHarbour is designed around the
              competition office, not around a single team.
            </p>
            <p className="mt-4 leading-7 text-muted-foreground">
              Administrators, competition operators, statistics editors, scorekeepers
              and club managers each get the access their job needs, and every change
              to a result, fixture, roster or club record is recorded with who made it.
            </p>
          </div>
          <div className="space-y-6">
            <ol className="space-y-3">
              {operatingSteps.map((step, index) => (
                <li key={step} className="flex gap-4 rounded-xl border bg-card p-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground">
                    {index + 1}
                  </span>
                  <span className="pt-1 text-sm font-medium leading-6">{step}</span>
                </li>
              ))}
            </ol>
            <ProductShotFigure shot="schedule" />
            <Link
              to="/walkthrough"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80"
            >
              See the full back office <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="border-y bg-muted/30 py-20">
        <div className="container mx-auto grid gap-6 px-4 lg:grid-cols-2">
          <article className="rounded-2xl border bg-card p-8 md:p-10">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <BarChart3 className="h-6 w-6" />
            </div>
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-primary">Turn audience into commercial value</p>
            <h2 className="mt-3 text-3xl font-bold">Give sponsors more than a logo in the footer</h2>
            <p className="mt-5 leading-7 text-muted-foreground">
              Place partners across high-value league surfaces. Track views and clicks
              by placement and date, then export a report your commercial team can share.
            </p>
            <ProductShotFigure shot="sponsor-report" className="mt-6" />
          </article>
          <article className="rounded-2xl border bg-card p-8 md:p-10">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Sparkles className="h-6 w-6" />
            </div>
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-primary">Make every match matter</p>
            <h2 className="mt-3 text-3xl font-bold">Turn match data into a season narrative</h2>
            <p className="mt-5 leading-7 text-muted-foreground">
              Sport-aware statistics, leaders, Power Index, and written match recaps
              give fans a reason to return between fixtures.
            </p>
          </article>
        </div>
      </section>

      <section id="sports" className="scroll-mt-24 py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Built around the statistics your sport uses</h2>
            <p className="mt-4 text-muted-foreground">
              Choose a sport to see its scoring, standings, positions, and player-stat experience.
            </p>
          </div>
          <div className="mx-auto mt-10 max-w-3xl"><SportSelectorWidget /></div>
        </div>
      </section>

      <section id="migration" className="scroll-mt-24 border-y bg-muted/30 py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-5xl rounded-2xl border bg-card p-8 md:p-12">
            <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
              <div>
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <FileUp className="h-6 w-6" />
                </div>
                <h2 className="text-3xl font-bold">Your next season does not start from zero</h2>
                <p className="mt-5 leading-7 text-muted-foreground">
                  We review your current data and help prepare teams, players, schedules,
                  and results for import before the competition goes live.
                </p>
              </div>
              <div className="space-y-5">
                {[
                  'Send a sample of your current competition data.',
                  'Review the structure, branding, and match-day workflow.',
                  'Launch with a clear checklist before the first fixture.',
                ].map((step, index) => (
                  <div key={step} className="flex items-center gap-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border font-semibold text-primary">{index + 1}</span>
                    <span className="font-medium">{step}</span>
                  </div>
                ))}
                <Button asChild variant="outline" className="mt-3 gap-2">
                  <Link to="/contact?intent=migration">Discuss your migration <ArrowRight className="h-4 w-4" /></Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="scroll-mt-24 py-20">
        <div className="container mx-auto px-4 text-center">
          <CalendarDays className="mx-auto h-9 w-9 text-primary" />
          <h2 className="mt-5 text-3xl font-bold tracking-tight md:text-4xl">One yearly price. No sales calls.</h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-muted-foreground">
            Pick a tier, start a 14-day free trial, and check out online. Every plan
            includes unlimited leagues and seasons, capped only by active teams.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Button asChild size="lg"><Link to="/pricing">View pricing</Link></Button>
          </div>
        </div>
      </section>

      <section className="border-y bg-muted/30 py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <Globe2 className="mx-auto h-9 w-9 text-primary" />
            <h2 className="mt-5 text-3xl font-bold">Your competition data remains yours</h2>
            <p className="mt-5 text-lg leading-8 text-muted-foreground">
              StatHarbour separates customer data, restricts administration to authenticated
              users, and provides exports for your records. Public competition data appears
              only on published league pages. Sponsor reporting remains restricted to organizers.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-primary py-20 text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <Trophy className="mx-auto h-10 w-10" />
          <h2 className="mt-5 text-3xl font-bold md:text-4xl">See how your competition would look</h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-primary-foreground/80">
            Explore a live, published competition with real teams, scores, standings, and stats.
            No account needed.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <DemoLink><Button size="lg" variant="secondary" className="w-full sm:w-auto">Explore the live match centre</Button></DemoLink>
          </div>
          <p className="mt-4 text-sm text-primary-foreground/70">
            Have questions first? <Link to="/contact" className="underline underline-offset-4 hover:text-primary-foreground">Contact us</Link>.
          </p>
        </div>
      </section>
    </main>

    <Footer variant="full" />
  </div>
  );
};

export default Index;
